<template>
  <nav class="navbar navbar-expand-lg navbar-light fixed-top">
    <div class="container-fluid">
      <!-- Logo (always visible) -->
      <div class="navbar-brand d-flex align-items-center" @click="handleLogoClick">
        <img
          src="@/assets/PV Smart Planner.png"
          :alt="$t('navbar.designerLogo')"
          height="32"
          class="d-inline-block align-text-center"
        />
        <div class="vertical-divider"></div>
        <div
          :style="{
            backgroundImage: 'var(--logo-image)',
            height: '32px',
            width: '80px',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }"
        ></div>
      </div>

      <!-- Right Side Menu -->
      <div class="d-flex align-items-center">
        <!-- Authenticated User Menu Items -->
        <template v-if="isAuthenticated">
          <button
            class="btn btn-link nav-text"
            @click="handleProjectsNavigation"
            :title="$t('navbar.home')"
          >
            {{ $t("nav.projects") }}
          </button>
          <button
            class="btn btn-link nav-text"
            @click="handleNavigation(goToProfile)"
            :title="$t('navbar.profile')"
          >
            {{ $t("nav.account") }}
          </button>
          <!-- Vertical Divider -->
          <div class="vertical-divider"></div>
        </template>
        <!-- Language Selector (always visible) -->
        <div class="language-selector">
          <select
            class="form-select"
            v-model="currentLanguage"
            @change="changeLanguage"
          >
            <option
              v-for="lang in languages"
              :key="lang.code"
              :value="lang.code"
            >
              {{ lang.name }}
            </option>
          </select>
        </div>
        <!-- Authenticated User Menu Items -->
        <template v-if="isAuthenticated">
          <button
            class="btn btn-link nav-icon"
            @click="handleNavigation(handleLogout)"
            :title="$t('navbar.logout')"
          >
            <i class="bi bi-box-arrow-right"></i>
          </button>
        </template>
      </div>
    </div>

    <!-- Navigation Confirmation Modal -->
    <div
      v-if="showNavigationModal"
      class="modal-overlay"
      @click="showNavigationModal = false"
    >
      <div class="modal-content" @click.stop>
        <h3 class="text-center mb-3">{{ $t("navbar.navigationModal.title") }}</h3>
        <p class="text-center mb-4">
          {{ $t("navbar.navigationModal.message") }}
        </p>
        <div class="d-flex justify-content-center gap-3">
          <button
            @click="showNavigationModal = false"
            class="btn btn-secondary px-4"
          >
            {{ $t("navbar.navigationModal.cancel") }}
          </button>
          <button @click="confirmNavigation" class="btn btn-primary px-4">
            {{ $t("navbar.navigationModal.confirm") }}
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth";
import { storeToRefs } from "pinia";

interface Language {
  code: string;
  name: string;
}

export default defineComponent({
  name: "NavbarComponent",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { locale } = useI18n();
    const authStore = useAuthStore();
    const { isAuthenticated } = storeToRefs(authStore);
    const currentLanguage = ref(locale.value);
    const showNavigationModal = ref(false);
    const pendingNavigation = ref<(() => void) | null>(null);

    const languages: Language[] = [
      { code: "it", name: "IT" },
      { code: "en", name: "EN" },
      { code: "es", name: "ES" },
      { code: "fr", name: "FR" },
      { code: "de", name: "DE" },
    ];

    const changeLanguage = (event: Event) => {
      const target = event.target as HTMLSelectElement;
      const selectedCode = target.value;
      if (languages.find((l) => l.code === selectedCode)) {
        currentLanguage.value = selectedCode;
        locale.value = selectedCode;
      }
    };

    const goToHome = () => {
      router.push("/projects");
    };

    const goToProfile = () => {
      router.push("/profile");
    };

    const handleLogout = async () => {
      try {
        await authStore.logout();
        router.push("/");
      } catch (error) {
        console.error("Logout failed:", error);
      }
    };

    const handleNavigation = (callback: () => void) => {
      // Check if user is currently on the project view page
      if (route.path.includes('/project')) {
        showNavigationModal.value = true;
        pendingNavigation.value = callback;
      } else {
        // If not on project page, proceed with navigation immediately
        callback();
      }
    };

    const handleProjectsNavigation = () => {
      // Check if already on projects list view
      if (route.path === '/projects') {
        console.log('Projects list view is already open');
        // Optionally scroll to top or show a message
        return;
      }
      
      // Check if user is currently on the project view page
      if (route.path.includes('/project')) {
        showNavigationModal.value = true;
        pendingNavigation.value = goToHome;
      } else {
        // If not on project page, proceed with navigation immediately
        goToHome();
      }
    };

    const confirmNavigation = () => {
      showNavigationModal.value = false;
      if (pendingNavigation.value) {
        pendingNavigation.value();
        pendingNavigation.value = null;
      }
    };

    const handleLogoClick = () => {
      const goToHome = () => {
        router.push("/");
      };
      handleNavigation(goToHome);
    };

    return {
      isAuthenticated,
      currentLanguage,
      languages,
      changeLanguage,
      goToHome,
      goToProfile,
      handleLogout,
      router,
      showNavigationModal,
      handleNavigation,
      handleProjectsNavigation,
      confirmNavigation,
      handleLogoClick,
    };
  },
});
</script>

<style scoped>
.navbar {
  padding: 10px 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1030;
  background-color: var(--color-white);
}

.vertical-divider {
  width: 1px;
  height: 24px;
  background-color: var(--color-light-gray);
  margin: 0 20px;
}

.language-selector {
  margin: 0 20px;
}

.form-select {
  padding: 6px 36px 6px 12px;
  font-size: 14px;
  border-radius: 2px;
  border-color: var(--color-light-gray);
  cursor: pointer;
}

.nav-text {
  padding: 8px 12px;
  color: var(--color-dark);
  font-size: 14px;
  text-decoration: none;
  border: none;
  background: none;
  margin-left: 8px;
  transition: all 0.2s;
}

.nav-text:hover {
  color: var(--primary-color);
  background: none;
}

.nav-icon {
  padding: 8px;
  color: var(--color-dark);
  font-size: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  margin-left: 8px;
  transition: all 0.2s;
}

.nav-icon:hover {
  color: var(--primary-color);
  background-color: var(--color-light);
}

.nav-icon i {
  line-height: 1;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
}

.modal-content {
  background-color: var(--color-white);
  padding: 24px;
  border-radius: 2px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  color: var(--color-dark);
}

.modal-content h3 {
  margin-top: 0;
  color: var(--color-dark);
  font-weight: 600;
}

.modal-content p {
  color: var(--color-dark);
  line-height: 1.5;
}

.modal-content .btn {
  min-width: 100px;
  font-weight: 500;
  border-radius: 2px;
}

.modal-content .btn-secondary {
  background-color: var(--color-gray);
  border-color: var(--color-gray);
  color: var(--color-white);
}

.modal-content .btn-secondary:hover {
  background-color: var(--color-dark-dark);
  border-color: var(--color-dark-dark);
}

.modal-content .btn-primary {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  color: var(--color-white);
}

.modal-content .btn-primary:hover {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  filter: brightness(0.9);
}
</style>
